import { apiClient } from './client';
import { WebSocketMessage } from '../types';

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

export interface WebSocketSubscription {
  type: string;
  handler: WebSocketEventHandler;
}

class WebSocketManager {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, Set<WebSocketEventHandler>> = new Map();
  // Ref-count подписок: канал может быть нужен нескольким компонентам
  // одновременно (Dashboard + Terminal оба держат 'exchanges'; несколько
  // эффектов — один tickers:<sym>). Без счётчика unsubscribe одного компонента
  // глушил живой канал у остальных. Шлём unsubscribe только когда счётчик → 0.
  private subscribedChannels: Map<string, number> = new Map();
  private reconnectAttempts = 0;
  // Без верхнего лимита — мы хотим переподключаться вечно при рестарте бэка,
  // сетевых блипах, идл-таймаутах прокси и т.п. Backoff капается на 15с.
  private reconnectDelayMin = 1000;
  private reconnectDelayMax = 15000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private shouldConnect = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private lastPongTime = 0;
  // Watchdog: если давно нет НИКАКИХ сообщений от сервера — рвём и реконнектимся.
  private staleWatchdog: ReturnType<typeof setInterval> | null = null;
  private connectionListeners: Set<(connected: boolean) => void> = new Set();

  // Наборы подписчиков создаются лениво в subscribe(); легаси-пресеты
  // v1-каналов (spread_update/execution_update/health_update) удалены —
  // бэкенд v2 шлёт spreads/tickers/orderbook/exchanges/global_top/alert.

  async connect(): Promise<void> {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.shouldConnect = true;
    this.isConnecting = true;

    try {
      const token = apiClient.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const wsUrl = `${apiClient.getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      this.ws = new WebSocket(wsUrl);
      // Сжатые кадры сканера приходят бинарными (см. wireName).
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);

    } catch (error) {
      this.isConnecting = false;
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.shouldConnect = false;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.staleWatchdog) {
      clearInterval(this.staleWatchdog);
      this.staleWatchdog = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Normal closure');
      this.ws = null;
    }

    this.notifyConnectionListeners(false);
  }

  subscribe(type: string, handler: WebSocketEventHandler): () => void {
    if (!this.subscriptions.has(type)) {
      this.subscriptions.set(type, new Set());
    }
    
    this.subscriptions.get(type)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.subscriptions.get(type);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }

  /** Имя канала «на проводе». Таблица сканера — 570 КБ JSON в секунду; сервер
   *  умеет слать её сжатой (`global_top_gz`, бинарный кадр, в ~8 раз меньше).
   *  Подмена живёт здесь, чтобы страницы по-прежнему подписывались на
   *  `global_top` и получали сообщения того же типа. Браузер без
   *  DecompressionStream остаётся на текстовом канале. */
  private wireName(channel: string): string {
    return channel === 'global_top' && typeof DecompressionStream !== 'undefined'
      ? 'global_top_gz' : channel;
  }

  /** v2 channel subscribe — speaks the {action,channels} protocol */
  subscribeChannels(channels: string[]): void {
    // Шлём 'subscribe' только для каналов, чей счётчик впервые стал 1 —
    // повторные подписчики лишь инкрементят ref-count.
    const newly: string[] = [];
    channels.forEach((c) => {
      const n = (this.subscribedChannels.get(c) || 0) + 1;
      this.subscribedChannels.set(c, n);
      if (n === 1) newly.push(c);
    });
    if (newly.length && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'subscribe', channels: newly.map(c => this.wireName(c)) }));
    }
    // иначе — отправим всё в onopen через flushPendingSubscriptions()
  }
  unsubscribeChannels(channels: string[]): void {
    // Декрементим ref-count; реально отписываемся (и шлём на сервер) только
    // когда последний потребитель ушёл (счётчик → 0).
    const gone: string[] = [];
    channels.forEach((c) => {
      const n = (this.subscribedChannels.get(c) || 0) - 1;
      if (n <= 0) {
        this.subscribedChannels.delete(c);
        gone.push(c);
      } else {
        this.subscribedChannels.set(c, n);
      }
    });
    if (gone.length && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'unsubscribe', channels: gone.map(c => this.wireName(c)) }));
    }
  }
  private flushPendingSubscriptions(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.subscribedChannels.size === 0) return;
    this.ws.send(JSON.stringify({ action: 'subscribe',
      channels: Array.from(this.subscribedChannels.keys()).map(c => this.wireName(c)) }));
  }

  onConnectionChange(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    
    // Immediately notify of current status
    listener(this.isConnected());

    // Return unsubscribe function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private handleOpen(): void {
    console.log('WebSocket connected');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.lastPongTime = Date.now();

    // Start ping/pong + stale watchdog
    this.startPing();
    this.startStaleWatchdog();

    this.notifyConnectionListeners(true);

    // re-send all pending channel subscriptions (after reconnect or initial subscribe-before-open)
    this.flushPendingSubscriptions();
  }

  private handleMessage(event: MessageEvent): void {
    if (typeof event.data !== 'string') {
      // Бинарный кадр = gzip от обычного JSON-сообщения. Распаковка
      // асинхронная; данные снимочные, поэтому порядок относительно соседних
      // сообщений не важен.
      this.lastPongTime = Date.now();
      void this.inflate(event.data as ArrayBuffer).then(
        text => this.dispatchText(text),
        error => console.error('Failed to inflate WebSocket frame:', error));
      return;
    }
    this.dispatchText(event.data);
  }

  private async inflate(buf: ArrayBuffer): Promise<string> {
    const stream = new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip'));
    return await new Response(stream).text();
  }

  private dispatchText(text: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(text);
      // Update last pong time for any message (serves as pong)
      if (message.type !== 'ping') {
        this.lastPongTime = Date.now();
      }

      // Handle pong specifically
      if (message.type === 'pong') {
        this.lastPongTime = Date.now();
        return;
      }

      // Dispatch to subscribers
      const handlers = this.subscriptions.get(message.type);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('Error in WebSocket message handler:', error);
          }
        });
      }

    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);
    this.isConnecting = false;
    this.ws = null;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.staleWatchdog) {
      clearInterval(this.staleWatchdog);
      this.staleWatchdog = null;
    }

    this.notifyConnectionListeners(false);

    // Реконнект при ЛЮБОМ close, пока shouldConnect: код 1000 приходит и при
    // graceful-рестарте uvicorn/прокси — раньше UI навсегда замирал до F5.
    // Намеренное отключение защищено shouldConnect=false в disconnect().
    if (this.shouldConnect) {
      this.scheduleReconnect();
    }
  }

  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    // isConnecting здесь НЕ сбрасываем: сокет может быть ещё в CONNECTING, и
    // параллельный connect() создал бы второй сокет (старый висел бы до
    // таймаута). onerror всегда сопровождается onclose — там и сброс.

    // Notify error subscribers
    const errorHandlers = this.subscriptions.get('error');
    if (errorHandlers) {
      errorHandlers.forEach(handler => {
        try {
          handler({
            type: 'error',
            timestamp: new Date().toISOString(),
            error: 'WebSocket connection error'
          });
        } catch (handlerError) {
          console.error('Error in error handler:', handlerError);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldConnect) return;
    if (this.reconnectTimer) return;  // уже запланировано

    this.reconnectAttempts++;
    // Exponential backoff capped at 15s. Без верхнего лимита по числу попыток —
    // продолжаем тыкаться пока shouldConnect=true.
    const delay = Math.min(
      this.reconnectDelayMin * Math.pow(1.7, this.reconnectAttempts - 1),
      this.reconnectDelayMax,
    );
    console.log(`[ws] reconnect attempt ${this.reconnectAttempts} in ${Math.round(delay)}ms`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldConnect) {
        this.connect();
      }
    }, delay);
  }

  private startPing(): void {
    if (this.pingInterval) clearInterval(this.pingInterval);
    // 10с (было 25с): ping ОБЯЗАН быть чаще порога watchdog'а, иначе на
    // странице без активных каналов watchdog рвал «тихое» соединение раньше,
    // чем уходил первый ping — вечный цикл реконнектов.
    const sendPing = () => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Backend WS protocol: subscribe / unsubscribe / ping в поле `action`.
        // Отправляем именно такой формат — иначе backend игнорирует и pong не приходит.
        this.send({ action: 'ping' });
      }
    };
    sendPing(); // первый ping сразу — lastPongTime получает свежий pong до первого тика watchdog'а
    this.pingInterval = setInterval(sendPing, 10000);
  }

  /**
   * Watchdog: если от сервера не было НИКАКИХ сообщений 20+ сек — соединение
   * мертво (TCP RST не дошёл, прокси-killing, etc). Закрываем — onclose триггерит
   * reconnect. Это страховка от "тихих" разрывов где readyState всё ещё OPEN.
   */
  private startStaleWatchdog(): void {
    if (this.staleWatchdog) clearInterval(this.staleWatchdog);
    let lastTick = Date.now();
    this.staleWatchdog = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTick;
      lastTick = now;
      if (!(this.ws && this.ws.readyState === WebSocket.OPEN)) return;
      // Таймеры спали: вкладка в фоне (Chrome душит setInterval до раза в
      // минуту) или ноутбук засыпал. Ping'и в это время не уходили, pong'ов не
      // было — «тишина 30 секунд» тут ничего не говорит о соединении. Раньше
      // сторож рвал ЖИВОЙ сокет, и при возврате на вкладку человека встречало
      // «Нет связи с сервером», хотя всё работало (09.10). Вместо разрыва —
      // пинг и новый отсчёт: мёртвое соединение сторож добьёт следующим кругом.
      if (gap > 15000) {
        this.lastPongTime = now;
        this.send({ action: 'ping' });
        return;
      }
      const stale = now - this.lastPongTime;
      // Порог = 3× ping-интервала (10с): переживает потерю пары pong'ов
      if (stale > 30000) {
        console.warn(`[ws] no messages from server for ${stale}ms — forcing reconnect`);
        try { this.ws.close(4000, 'stale'); } catch {}
      }
    }, 5000);
  }

  /** Вкладку снова открыли: соединение проверяем сразу, а не ждём таймеров —
   *  если оно живо, пинг это подтвердит; если нет — переподключаемся без
   *  накопленной паузы между попытками. */
  private onVisible = (): void => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!this.shouldConnect) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.lastPongTime = Date.now();
      this.send({ action: 'ping' });
      return;
    }
    if (this.isConnecting) return;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.reconnectAttempts = 0;
    void this.connect();
  };

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisible);
      window.addEventListener('online', this.onVisible);
    }
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }
}

export const wsManager = new WebSocketManager();